import React from 'react';

type ContentFitIframeProps = {
    html: string;
    maxWidth: number;
    onResize: (width: number) => string;
};

function getWindowSize() {
    const { innerWidth: width, innerHeight: height } = window;
    return { width, height };
}

function getIframeSize(iframe: HTMLIFrameElement | null) {
    if (!iframe?.contentWindow) return { width: 0, height: 0 };
    const body = iframe.contentWindow.document.body;
    if (!body) return { width: 0, height: 0 };
    return { width: body.scrollWidth, height: body.scrollHeight + 32 };
}

export function ContentFitIframe({
    html,
    maxWidth,
    onResize,
}: ContentFitIframeProps) {
    const ref = React.useRef<HTMLIFrameElement>(null);
    const [currentHtml, setCurrentHtml] = React.useState(html);
    const [iframeSize, setIframeSize] = React.useState(
        getIframeSize(ref.current)
    );

    React.useEffect(() => {
        function handleResize() {
            const size = getWindowSize();
            setCurrentHtml(onResize(size.width));
            setIframeSize({
                ...getIframeSize(ref.current),
                width: size.width < maxWidth ? size.width : maxWidth,
            });
        }
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const onLoad = () => {
        if (!ref.current?.contentWindow) return;
        setIframeSize(getIframeSize(ref.current));
        setCurrentHtml(onResize(getWindowSize().width));
    };

    return (
        <iframe
            ref={ref}
            onLoad={onLoad}
            width={`${iframeSize.width}px`}
            height={`${iframeSize.height}px`}
            srcDoc={currentHtml}
            scrolling="no"
        />
    );
}
